// @ts-check
import React, { useCallback, useMemo, useState } from 'react';
import '@patternfly/react-core/dist/styles/base.css';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import _ from 'lodash';


import {
  modelIdWithoutTemplateId
} from 'components/Admin/Model/utils/model';
import useAPI from 'services/useAPI';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import createMutationRequest from 'utils/createMutationRequest';
import { useTranslation } from 'localization/useTranslation';
import Tl from 'localization/LocalizationKeys';
import SimpleTable, { SortDirection } from 'components/Common/SimpleTable/SimpleTable';
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal';
import useAlerts from 'utils/hooks/useAlerts';
import ContentPage from 'components/ContentView/ContentPage/ContentPage';
import { useBindingsByModel } from '../queries';
import queryString from 'query-string';
import { modelKeys } from '../queries';

/**
 * @typedef {import('../queries').BindingType} BindingType
 */

/**
 * @type {import('components/Common/SimpleTable/SimpleTable').ColumnType<BindingType>[]}
 */
const columns = [
  {
    label: Tl.model.version,
    path: 'id',
    isSortable: true,
    searchItem: true
  },
  {
    label: Tl.common.name,
    path: 'displayName',
    isSortable: true,
    searchItem: true
  },
  {
    label: Tl.model.binding.assetModel,
    path: 'assetModelId',
    isSortable: true,
    searchItem: true
  },
  {
    label: Tl.model.binding.sources,
    path: 'sources',
    isSortable: true,
    searchItem: true
  },
  {
    label: Tl.common.draft,
    path: 'draft',
    isSortable: true,
    searchItem: true,
    render: val => val ? 'true' : 'false'
  }
];

/**
 * @type {import('components/Common/SimpleTable/SimpleTable').SortByType<BindingType>}
 * @readonly
 */
const DefaultSortBy = {
  path: 'displayName',
  direction: SortDirection.DESC
};


const BindingDescriptionList = ({ breadcrumbs }) => {
  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const modelId = modelIdWithoutTemplateId(params.modelId);

  const [hasError] = useState(false);
  const api = useAPI();
  const T = useTranslation();
  const queryClient = useQueryClient();

  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [alerts, , addErrorAlert] = useAlerts();

  const {
    bindings,
    isLoading,
    bindingsQuery,
    bindingDraftsQuery
  } = useBindingsByModel(modelId, true);

  const confirmedDeleteQuery = useMutation({
    mutationFn: createMutationRequest(api.core.deleteBindingItem),
    onMutate: useCallback((binding) => {
      const oldData = queryClient.getQueryData(modelKeys.bindingByModelId(modelId));
      queryClient.setQueryData(modelKeys.bindingByModelId(modelId), old => {
        return _.reject(old, { id: binding.id });
      });
      return { binding, oldData };
    }, [queryClient, modelId]),
    onSuccess: useCallback(() => {
      setConfirmDeleteItem(null);
      bindingsQuery.refetch();
    }, [bindingsQuery]),
    onError: useCallback((error, unusedVariables, context) => {
      console.error(error);
      addErrorAlert(T.common.request.delete.error);
      queryClient.setQueryData(modelKeys.bindingByModelId(modelId), context.oldData);
      bindingsQuery.refetch();
    }, [T.common.request.delete.error, addErrorAlert, bindingsQuery, modelId, queryClient])
  });


  const confirmedDeleteDraftQuery = useMutation({
    mutationFn: createMutationRequest(api.core.deleteBindingDraftItem),
    onMutate: useCallback((binding) => {
      const oldData = queryClient.getQueryData(modelKeys.bindingByModelId(modelId));
      queryClient.setQueryData(modelKeys.bindingByModelId(modelId), old => {
        return _.reject(old, { id: binding.id });
      });
      return { binding, oldData };
    }, [queryClient, modelId]),
    onSuccess: useCallback(() => {
      setConfirmDeleteItem(null);
      bindingDraftsQuery.refetch();
    }, [bindingDraftsQuery]),
    onError: useCallback((error, unusedVariables, context) => {
      console.error(error);
      addErrorAlert(T.common.request.delete.error);
      queryClient.setQueryData(modelKeys.bindingByModelId(modelId), context.oldData);
      bindingDraftsQuery.refetch();
    }, [T.common.request.delete.error, addErrorAlert, bindingDraftsQuery, modelId, queryClient])
  });


  // const [confirmedDeleteDraft, isDeletingDraft] = useApiCall({
  //   apiCall: api.core.deleteBindingDraftItem,
  //   onSuccess: useCallback(() => {
  //     addSuccessAlert(T.common.request.delete.success);
  //     setConfirmDeleteItem(oldDeleteItem => {
  //       setBindings(oldModels => {
  //         return _.reject(oldModels, { id: oldDeleteItem.id, draft: oldDeleteItem.draft });
  //       });
  //       return null;
  //     });
  //   }, [T.common.request.delete.success, addSuccessAlert]),
  //   onError: useCallback(() => {
  //     addErrorAlert(T.common.request.delete.error);
  //   }, [T.common.request.delete.error, addErrorAlert])
  // });

  // const [confirmedDelete, isDeleting] = useApiCall({
  //   apiCall: api.core.deleteBindingItem,
  //   onSuccess: useCallback(() => {
  //     addSuccessAlert(T.common.request.delete.success);
  //     setConfirmDeleteItem(oldDeleteItem => {
  //       setBindings(oldModels => {
  //         return _.reject(oldModels, { id: oldDeleteItem.id, draft: oldDeleteItem.draft });
  //       });
  //       return null;
  //     });
  //   }, [T.common.request.delete.success, addSuccessAlert]),
  //   onError: useCallback(() => {
  //     addErrorAlert(T.common.request.delete.error);
  //   }, [T.common.request.delete.error, addErrorAlert])
  // });

  const onRowClick = useCallback((binding) => {
    const parsed = queryString.parse(location.search);
    history.push({
      pathname: `${location.pathname}/${binding.id}`,
      search: queryString.stringify({ ...parsed, bindingDraft: binding.draft }),
      state: {
        draft: binding.draft,
        assetModelId: binding.assetModelId
      }
    });
  }, [history, location.pathname, location.search]);

  const onDelete = useCallback(binding => setConfirmDeleteItem(binding), []);

  const cancelDelete = useCallback(() => setConfirmDeleteItem(null), []);

  const onEditVersion = useCallback((binding) => {
    const parsed = queryString.parse(location.search);
    history.push({
      pathname: `${location.pathname}/${binding.id}`,
      search: queryString.stringify({ ...parsed, bindingDraft: binding.draft })
    });
  }, [history, location.pathname, location.search]);


  const actions = useMemo(/** @return {import('components/Common/SimpleTable/SimpleTable').ActionType<BindingType>[]} */() => [
    {
      title: T.common.delete,
      onClick: onDelete
    },
    {
      title: T.model.list.edit.version,
      onClick: onEditVersion,
      isDisabled: (item) => {
        return (item.draft === true) ? true : false;
      }
    }
  ], [T.common.delete, T.model.list.edit.version, onDelete, onEditVersion]);

  /** @type {import('components/Common/SimpleTable/SimpleTable').GlobalAction[]} */
  const globalActions = [
    {
      label: Tl.common.add,
      onClick: () => {
        const parsed = queryString.parse(location.search);
        history.push({
          pathname: `${location.pathname}/new-binding`,
          search: queryString.stringify({ ...parsed, bindingDraft: true })
        });
      }
    }
  ];

  return (
    <ContentPage breadcrumb={breadcrumbs} help={'admin/models/bindings/list'}>
      { alerts }
      <SimpleTable
        actions={actions}
        isLoading={isLoading && _.isEmpty(bindings)}
        onRowClick={onRowClick}
        data={bindings}
        columns={columns}
        sortBy={DefaultSortBy}
        hasError={hasError}
        errorText={T.common.error.failed.loading.list}
        globalActions={globalActions}
        compact
      />
      <ConfirmDeleteModal
        isLoading={confirmedDeleteDraftQuery.isLoading || confirmedDeleteQuery.isLoading}
        onConfirm={confirmDeleteItem?.draft ? confirmedDeleteDraftQuery.mutate : confirmedDeleteQuery.mutate}
        item={confirmDeleteItem}
        itemName={`${confirmDeleteItem?.displayName} (${confirmDeleteItem?.id})` ?? T.common.noname}
        onCancel={cancelDelete}
      />
    </ContentPage>
  );
};

export default BindingDescriptionList;
